import { ReadonlyVec3, vec3 } from 'gl-matrix';
import { memo, ReactNode, useCallback, useEffect, useRef } from 'react';
import { useRecoilCallback } from 'recoil';
import {
    IntensityRegion,
    IntensityRegionDraft,
    rectangleFrame,
    regionBetween,
    regionCenteredAt,
    regionCenterMm,
    RegionGeometry,
    regionHasArea,
    regionInFrame,
} from '~/domain/intensity/intensityRegion';
import { PointMm, toPointMm } from '~/domain/geometry/patientGeometry';
import { paneImageOf } from '~/engine/view/paneImage';
import {
    CanvasPoint,
    imageToPaneFrame,
    paneToImageFrame,
    viewShowsPlane,
} from '~/engine/view/planeViewGeometry';
import { regionIntensityStatistics } from '~/engine/data/image/regionIntensityStatistics';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { useIsDoseShown } from '~/hooks/dose/useDoseVisibility';
import { useImageForSeries } from '~/hooks/image/useImageForSeries';
import { useIsObliqueToImage } from '~/hooks/useAnnotationGoTo';
import {
    AnnotationPointDrag,
    ontoShownPane,
    useAnnotationPointDrag,
    usePlacedAnnotation,
} from '~/hooks/viewer/annotationOverlay';
import { updateAnnotation } from '~/state/atoms/annotationListAtoms';
import { intensityRegionListState } from '~/state/atoms/intensityToolAtoms';
import { INTENSITY_FAMILY_COLOR } from '~/utils/annotationKinds';
import { formatRegionLabel, formatRegionSize } from '~/utils/measurementFormat';
import {
    CanvasProjection,
    canvasProjection,
    DASHED,
    HandleCursor,
    HIT_BAND,
    PlacedAnnotationProps,
    placedShapeStyle,
    PlacedShape,
    SHAPE_STROKE_PX,
    ValueLabel,
} from '../AnnotationOverlaySvg';

const ANCHOR_DOT_PX = 3.5;
/**
 * While the region being placed spans less than this on screen, its size
 * label is not drawn: it would sit on the first point and flicker during the
 * first pixels of the drag.
 */
const DRAFT_LABEL_MIN_SPAN_PX = 24;

/** Which way a press on a handle edits a region. */
type RegionHandle = 'move' | 'resize';

interface RegionDrag {
    readonly id: string;
    readonly handle: RegionHandle;
    /**
     * A move keeps the region in the plane through its centre, here; a
     * resize spans the region from here to the cursor: from a circle's
     * centre, or from the rectangle corner opposite the one dragged.
     */
    readonly anchorMm: PointMm;
    /** The directions a rectangle's sides keep while it is resized. */
    readonly axes: readonly [ReadonlyVec3, ReadonlyVec3];
}

/** The centre alone decides which slice a region lies on. */
function regionPoints(region: RegionGeometry): readonly PointMm[] {
    return [regionCenterMm(region)];
}

/** A region as drawn on a tile, in canvas px. */
interface RegionOnCanvas {
    readonly outline: (props: object) => ReactNode;
    readonly center: CanvasPoint;
    /** The handles after the centre's. */
    readonly resizeHandles: readonly CanvasPoint[];
    readonly resizeCursor: (index: number) => Exclude<HandleCursor, 'move'>;
    /** The point the label hangs above. */
    readonly top: CanvasPoint;
    /** The region's extent on screen, px. */
    readonly spanPx: number;
}

/**
 * `region` projected onto `canvas`. A circle's on-screen radius is a
 * patient-space point one radius along the canvas's X axis, projected, so it
 * tracks zoom with no pixels-per-mm assumption.
 */
function onCanvas(
    region: RegionGeometry,
    { viewXDirection, toCanvas }: CanvasProjection
): RegionOnCanvas {
    if (region.shape === 'circle') {
        const center = toCanvas(region.centerMm);
        const edgeMm = toPointMm(
            vec3.scaleAndAdd(
                vec3.create(),
                region.centerMm,
                viewXDirection,
                region.radiusMm
            )
        );
        const edge = toCanvas(edgeMm);
        const r = Math.hypot(edge.x - center.x, edge.y - center.y);
        return {
            outline: (props) => (
                <circle cx={center.x} cy={center.y} r={r} {...props} />
            ),
            center,
            resizeHandles: [{ x: center.x + r, y: center.y }],
            resizeCursor: () => 'ew-resize',
            top: { x: center.x, y: center.y - r },
            spanPx: 2 * r,
        };
    }
    const corners = region.cornersMm.map(toCanvas);
    const center = toCanvas(regionCenterMm(region));
    const points = corners.map(({ x, y }) => `${x},${y}`).join(' ');
    const top = corners.reduce((a, b) => (b.y < a.y ? b : a));
    return {
        outline: (props) => <polygon points={points} {...props} />,
        center,
        resizeHandles: corners,
        resizeCursor: (index) => {
            const { x, y } = corners[index];
            return (x - center.x) * (y - center.y) > 0
                ? 'nwse-resize'
                : 'nesw-resize';
        },
        top: { x: center.x, y: top.y },
        spanPx: Math.hypot(
            corners[2].x - corners[0].x,
            corners[2].y - corners[0].y
        ),
    };
}

const aboveRegion = (top: CanvasPoint): CanvasPoint => ({
    x: top.x,
    y: top.y - 6,
});

/** The latest cursor position of a drag, waiting for its animation frame. */
interface PendingRegionMove {
    readonly drag: RegionDrag;
    readonly pointMm: PointMm;
    readonly view: PlaneView;
}

/**
 * Dragging a placed region by its centre (move) or a resize handle: a
 * circle's edge, or a rectangle's corner, the opposite corner staying put.
 * The dragged point is kept in the region's own plane, so an edit cannot
 * move it to another slice. The figures are read from the image the pane
 * shows, in the pane's frame, and the region is stored in that image's own
 * frame.
 *
 * Mouse moves arrive faster than frames are drawn, and every update re-reads
 * the voxels inside the region, so the moves are applied once per animation
 * frame. The shape and its figures are updated in the same step, so the
 * figures shown never belong to a region other than the one on screen.
 */
export function useRegionDrag(
    view: PlaneView | null
): AnnotationPointDrag<RegionDrag> {
    const pendingMove = useRef<PendingRegionMove | null>(null);
    const scheduledFrame = useRef<number | null>(null);

    const applyPendingMove = useRecoilCallback(
        (callback) => () => {
            scheduledFrame.current = null;
            const move = pendingMove.current;
            pendingMove.current = null;
            if (!move) {
                return;
            }
            const { drag, pointMm, view: dragView } = move;
            const pane = paneImageOf(dragView);
            updateAnnotation(
                callback,
                intensityRegionListState,
                drag.id,
                (region) => {
                    // The view can have changed image since the move was
                    // queued, to another series or to this one re-imported
                    // and filling again; the edit is refused as placement
                    // refuses it.
                    if (
                        pane.kind === 'none' ||
                        !viewShowsPlane(dragView, region.plane) ||
                        !pane.image.parent.isComplete
                    ) {
                        return region;
                    }
                    const onPane = regionInFrame(
                        region,
                        imageToPaneFrame(pane)
                    );
                    const edited =
                        drag.handle === 'move'
                            ? regionCenteredAt(onPane, pointMm)
                            : regionBetween(
                                  region.shape,
                                  drag.anchorMm,
                                  pointMm,
                                  ...drag.axes
                              );
                    // A resize that would collapse the region is refused,
                    // the same rule placement applies.
                    if (!regionHasArea(edited)) {
                        return region;
                    }
                    const stored = regionInFrame(
                        { ...edited, plane: onPane.plane },
                        paneToImageFrame(pane)
                    );
                    return {
                        ...stored,
                        id: region.id,
                        plane: region.plane,
                        visible: region.visible,
                        stats: regionIntensityStatistics(
                            pane.image,
                            edited,
                            onPane.plane.viewDirection
                        ),
                    };
                }
            );
        },
        []
    );

    const movePoint = useCallback(
        (drag: RegionDrag, pointMm: PointMm, dragView: PlaneView) => {
            pendingMove.current = { drag, pointMm, view: dragView };
            if (scheduledFrame.current === null) {
                scheduledFrame.current =
                    requestAnimationFrame(applyPendingMove);
            }
        },
        [applyPendingMove]
    );

    // A drag outliving the overlay has nothing left to update.
    useEffect(
        () => () => {
            if (scheduledFrame.current !== null) {
                cancelAnimationFrame(scheduledFrame.current);
            }
        },
        []
    );

    return useAnnotationPointDrag(view, movePoint);
}

/** How a press on handle `handle` of `region`, as on the pane, edits it. */
function dragFrom(
    id: string,
    region: IntensityRegion,
    handle: number,
    view: PlaneView
): RegionDrag {
    if (handle !== 0 && region.shape === 'rectangle') {
        const { u, v } = rectangleFrame(region.cornersMm);
        return {
            id,
            handle: 'resize',
            anchorMm: region.cornersMm[(handle + 1) % 4],
            axes: [u, v],
        };
    }
    return {
        id,
        handle: handle === 0 ? 'move' : 'resize',
        anchorMm: regionCenterMm(region),
        axes: [view.viewXDirection, view.viewYDirection],
    };
}

/**
 * Draws one placed region on a tile, labelled with its size, mean and SD
 * from its own image. The intensity is left out on a plane oblique to that
 * image, as the label has no room for the oblique mark (SR-WOR-H-17-5), and
 * while a dose is shown, where it would read as a dose statistic.
 */
function IntensityRegionOverlay(props: PlacedAnnotationProps) {
    const { id, view, frame } = props;
    const { beginDrag, dragging } = useRegionDrag(view);
    const imageForSeries = useImageForSeries();
    const isObliqueToImage = useIsObliqueToImage();
    const doseShown = useIsDoseShown();
    const placed = usePlacedAnnotation(
        props,
        intensityRegionListState,
        regionInFrame,
        regionPoints,
        dragging !== null
    );
    if (!placed) {
        return null;
    }
    const { stored, onPane: region } = placed;
    const drawn = onCanvas(region, canvasProjection(view, frame));
    return (
        <PlacedShape
            {...props}
            placed={placed}
            mode={region.shape}
            label={formatRegionLabel(
                stored,
                doseShown || isObliqueToImage(stored)
                    ? undefined
                    : imageForSeries(stored.plane.seriesInstanceUid)
            )}
            labelAt={aboveRegion(drawn.top)}
            labelAbove
            bands={drawn.outline(HIT_BAND)}
            handles={[drawn.center, ...drawn.resizeHandles]}
            handleCursors={[
                'move',
                ...drawn.resizeHandles.map((_, index) =>
                    drawn.resizeCursor(index)
                ),
            ]}
            beginDrag={(event, handle) =>
                beginDrag(event, dragFrom(id, region, handle, view))
            }
        >
            {drawn.outline({
                fill: 'none',
                stroke: INTENSITY_FAMILY_COLOR,
                ...placedShapeStyle(placed),
            })}
        </PlacedShape>
    );
}

export default memo(IntensityRegionOverlay);

/**
 * Draws the region being placed: a dot at the first point, and the region
 * following the cursor, sized once it is large enough to label.
 */
export function renderDraft(
    view: PlaneView,
    canvas: CanvasProjection,
    stored: IntensityRegionDraft | null
) {
    const draft = stored && ontoShownPane(view, stored, regionInFrame);
    if (!draft) {
        return null;
    }
    const drawn = onCanvas(draft, canvas);
    const anchor = canvas.toCanvas(
        draft.shape === 'circle' ? draft.centerMm : draft.cornersMm[0]
    );
    return (
        <g key="draft">
            {drawn.outline({
                fill: 'none',
                stroke: INTENSITY_FAMILY_COLOR,
                strokeWidth: SHAPE_STROKE_PX,
                strokeDasharray: DASHED,
            })}
            <circle
                cx={anchor.x}
                cy={anchor.y}
                r={ANCHOR_DOT_PX}
                fill={INTENSITY_FAMILY_COLOR}
            />
            {drawn.spanPx > DRAFT_LABEL_MIN_SPAN_PX && (
                <ValueLabel
                    at={aboveRegion(drawn.top)}
                    text={formatRegionSize(draft)}
                    color={INTENSITY_FAMILY_COLOR}
                    above
                />
            )}
        </g>
    );
}
